import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Download, FileText, CheckCircle, Clock,
  ChevronDown, ChevronUp, Brain, Heart, Star, MessageCircle,
  Compass, Layers, Lightbulb, Gem, Target, Calendar, Award,
  Share2, Lock, Printer, Wand2,
} from "lucide-react";
import { WorksheetPersonalizeDialog } from "../components/WorksheetPersonalizeDialog";
import { downloadWorksheetPdf } from "../components/WorksheetEditorDialog";
import { toast } from "sonner";
import { API } from "../App";
import { getExerciseInfo } from "../lib/exerciseData";
import { useI18n } from "../lib/i18n";

const ICON_MAP = {
  emotion_register: Brain, mood_tracking: Heart, gratitude: Star,
  assertiveness: MessageCircle, values_exploration: Compass,
  needs_exploration: Layers, problem_solving: Lightbulb,
  qualities_exploration: Gem, capabilities_exploration: Target,
  behavioral_activation: Calendar, self_reinforcement: Award,
};

const fmtDate = (d, locale = "fr") => {
  if (!d) return "";
  try {
    return new Intl.DateTimeFormat(locale === "fr" ? "fr-FR" : locale === "es" ? "es-ES" : "en-US", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    }).format(new Date(d));
  } catch { return d.slice(0, 16); }
};

const PatientExercisesPage = ({ user, logout, selfView }) => {
  const { patientId } = useParams();
  const { t, locale } = useI18n();
  const EXERCISE_INFO = getExerciseInfo(t);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});
  const [downloading, setDownloading] = useState(false);
  const [togglingShare, setTogglingShare] = useState(null);
  const isTherapistViewingPatient = user?.role === "therapist" && !selfView && patientId;
  const isPatientView = user?.role === "patient" || selfView;

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [patientId, user]);

  const fetchData = async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem("session_token");
      const url = isTherapistViewingPatient
        ? `${API}/therapist/patient-exercises/${patientId}`
        : `${API}/patient/exercises-history`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleShare = async (exerciseType, entryId, newShared) => {
    if (!entryId || togglingShare) return;
    setTogglingShare(entryId);
    try {
      await axios.put(`${API}/patient/toggle-share`, {
        entry_id: entryId,
        exercise_type: exerciseType,
        shared: newShared,
      }, { withCredentials: true });
      // Update local state
      setData(prev => {
        if (!prev?.standalone) return prev;
        const newStandalone = { ...prev.standalone };
        if (newStandalone[exerciseType]) {
          newStandalone[exerciseType] = newStandalone[exerciseType].map(e => {
            const eid = e.entry_id || e.record_id || e.session_id;
            if (eid === entryId) return { ...e, shared_with_therapist: newShared };
            return e;
          });
        }
        return { ...prev, standalone: newStandalone };
      });
    } catch (e) {
      console.error("Toggle share error:", e);
    } finally {
      setTogglingShare(null);
    }
  };

  const downloadPdf = async (type, id) => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("session_token");
      const targetId = isTherapistViewingPatient ? patientId : user.user_id;
      const url = type === "full"
        ? `${API}/export/pdf/patient/${targetId}?days=90`
        : `${API}/export/pdf/exercise/${id}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = type === "full" ? `rapport_patient.pdf` : `exercice_${id}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      console.error("PDF download error:", e);
    } finally {
      setDownloading(false);
    }
  };

  const [paperBusy, setPaperBusy] = useState("");
  const [personalize, setPersonalize] = useState(null);
  const downloadPaper = async (exerciseType) => {
    setPaperBusy(exerciseType);
    try {
      const res = await axios.get(`${API}/patient/exercise-worksheets/${exerciseType}/pdf`, { withCredentials: true, responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      link.download = `${t("patientExercises.paperVersion")}-${exerciseType}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      toast.error(t("patientExercises.paperVersionError"));
    } finally {
      setPaperBusy("");
    }
  };

  const renderResponseData = (respData) => {
    if (!respData) return null;
    if (typeof respData === "string") return <p className="text-sm text-muted-foreground">{respData}</p>;
    if (typeof respData === "object" && !Array.isArray(respData)) {
      const entries = Object.entries(respData).filter(([k, v]) => !SKIP_KEYS.has(k) && v != null && v !== "");
      if (entries.length === 0) return null;
      return (
        <div className="space-y-1.5">
          {entries.map(([key, val]) => (
            <div key={key}>{renderValue(key, val)}</div>
          ))}
        </div>
      );
    }
    if (Array.isArray(respData)) {
      return (
        <div className="space-y-1">
          {respData.map((item, i) => (
            <div key={i}>{typeof item === "object" ? renderValue(`item_${i}`, item) : <p className="text-sm">{String(item)}</p>}</div>
          ))}
        </div>
      );
    }
    return null;
  };

  const FIELD_LABELS = {
    situation: "Situation", automatic_thoughts: "Pensees automatiques", automatic_thought: "Pensee automatique",
    emotions: "Emotions", emotion_intensity: "Intensite", emotion_intensity_before: "Intensite avant",
    emotion_intensity_after: "Intensite apres", new_emotion_intensity: "Nouvelle intensite",
    balanced_thought: "Pensee equilibree", alternative_thought: "Pensee alternative",
    socratic_q1: "Question socratique 1", socratic_q2: "Question socratique 2", socratic_q3: "Question socratique 3",
    externalization_letter: "Lettre d'externalisation", evidence_for: "Arguments pour", evidence_against: "Arguments contre",
    distortions: "Distorsions cognitives", action_plan: "Plan d'action",
    currentStyle: "Style actuel", descD: "Decrire (D)", descE: "Exprimer (E)", descS: "Specifier (S)",
    descC: "Consequences (C)", practiceResponse: "Reponse assertive", reflection: "Reflexion",
    mood_level: "Niveau d'humeur", notes: "Notes", items: "Elements",
    accomplishment: "Accomplissement", difficulty_level: "Difficulte", emotions_felt: "Emotions ressenties",
    reward_chosen: "Recompense choisie", problem: "Probleme", goals: "Objectifs",
    solutions: "Solutions", selectedSolution: "Solution choisie", actionPlan: "Plan d'action",
    qualities: "Qualites", capabilities: "Competences",
    domains: "Domaines", needs: "Besoins", importance: "Importance", alignment: "Alignement",
    satisfaction: "Satisfaction", description: "Description", actions: "Actions", rating: "Note",
    activities: "Activites", date: "Date", duration: "Duree", duration_minutes: "Duree (min)",
    meditation_type: "Type de meditation", technique: "Technique", calm_level: "Niveau de calme",
    thought: "Pensee", fusion_before: "Fusion avant", fusion_after: "Fusion apres",
    exercise_type: "Type d'exercice", comfort_level: "Niveau de confort",
    self_criticism: "Auto-critique", compassionate_response: "Reponse bienveillante",
    sleep_quality: "Qualite du sommeil", intention: "Intention", checked: "Coche",
    task_name: "Tache", completed: "Termine", focus_rating: "Concentration", task: "Tache",
    pleasure_rating: "Plaisir", mastery_rating: "Maitrise",
    // ACT exercises
    hooks: "Hamecons", away_moves: "Mouvements d'eloignement", toward_moves: "Mouvements d'approche",
    value: "Valeur", goal: "Objectif", smallest_step: "Plus petit pas", obstacle: "Obstacle",
    trigger: "Declencheur", acknowledge: "Reconnaitre", connect: "Se connecter", engage: "S'engager",
    emotion: "Emotion", body_location: "Localisation corporelle", observe: "Observer",
    breathe_into: "Respirer dans", make_room: "Faire de la place",
    important: "Important", inner_obstacles: "Obstacles internes",
    away_behaviors: "Comportements d'eloignement", toward_behaviors: "Comportements d'approche",
    current_thoughts: "Pensees actuelles", current_emotions: "Emotions actuelles",
    notice_observing: "Observer", still_here: "Toujours la",
    behavior: "Comportement", functions: "Fonctions", feeling: "Ressenti", response: "Reponse",
    // TDAH kids
    location: "Lieu", sensations: "Sensations", intensity: "Intensite", boredom: "Ennui",
    urge: "Impulsivite", action_taken: "Action prise", feeling_after: "Ressenti apres",
    feeling_during: "Ressenti pendant", activity: "Activite",
    scenario: "Scenario", usual_behavior: "Comportement habituel", turbo_says: "Turbo dit",
    outcome: "Resultat", mission_activity: "Activite mission", result: "Resultat",
    steps_completed: "Etapes completees",
    color: "Couleur", shape: "Forme", texture: "Texture", noise_level: "Niveau de bruit", signal: "Signal",
    periods: "Periodes", energy: "Energie", need: "Besoin", name: "Nom",
    superpowers_known: "Super-pouvoirs connus", superpowers_custom: "Super-pouvoirs personnels",
    questions: "Questions", turbo_helps: "Turbo aide", brake_tired: "Frein fatigue",
    victory: "Victoire", proud_because: "Fier parce que",
    task_a1: "Tache A", task_b1: "Tache B", task_c1: "Tache C", first_action: "Premiere action",
    entries: "Entrees", data: "Donnees", responses: "Reponses", completed_phrases: "Phrases completees",
    text: "Texte", pros: "Pour", cons: "Contre", step: "Etape", deadline: "Echeance",
    quality: "Qualite", example: "Exemple", source: "Source",
    capability: "Competence", evidence: "Preuve", level: "Niveau",
    effectiveness: "Efficacite", awareness: "Prise de conscience",
    after_feeling: "Ressenti apres", values_at_stake: "Valeurs en jeu",
    chosen_action: "Action choisie", category: "Categorie",
    timeSlot: "Creneau horaire",
    // Frontend form field names (older data may use these)
    current_style: "Style actuel", describe: "Decrire (D)", express: "Exprimer (E)",
    specify: "Specifier (S)", consequences: "Consequences (C)",
    reformulated_response: "Reponse assertive",
    chosen_solution: "Solution choisie", action_steps: "Plan d'action",
    pros_cons: "Pour et Contre",
  };

  const SKIP_KEYS = new Set(["user_id", "entry_id", "record_id", "session_id", "created_at", "updated_at", "shared_with_therapist", "not_shared", "assignment_id", "_id"]);

  const renderValue = (key, val, depth = 0) => {
    if (val === null || val === undefined || val === "") return null;
    const label = FIELD_LABELS[key] || key.replace(/_/g, " ").replace(/([A-Z])/g, " $1").trim();
    const indent = depth > 0 ? "ml-3 pl-2 border-l-2 border-muted/50" : "";

    if (typeof val === "boolean") {
      return <p className={`${indent}`}><span className="font-medium">{label} :</span> {val ? "Oui" : "Non"}</p>;
    }
    if (typeof val === "number") {
      return <p className={`${indent}`}><span className="font-medium">{label} :</span> {val}{key.includes("intensity") || key.includes("level") || key.includes("rating") || key === "mood_level" || key === "calm_level" || key === "boredom" || key === "urge" || key === "energy" ? "/10" : ""}</p>;
    }
    if (typeof val === "string") {
      if (val.match(/^\d{4}-\d{2}-\d{2}/)) {
        try { return <p className={`${indent}`}><span className="font-medium">{label} :</span> {new Date(val).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}</p>; }
        catch { /* fall through */ }
      }
      const isPositive = key === "balanced_thought" || key === "alternative_thought" || key === "practiceResponse" || key === "compassionate_response" || key === "toward_moves" || key === "toward_behaviors" || key === "result" || key === "victory";
      return <p className={`${indent} ${isPositive ? "text-green-700 dark:text-green-400" : ""}`}><span className="font-medium">{label} :</span> {val.slice(0, 300)}</p>;
    }
    if (Array.isArray(val)) {
      if (val.length === 0) return null;
      if (typeof val[0] === "string" || typeof val[0] === "number") {
        return <p className={`${indent}`}><span className="font-medium">{label} :</span> {val.join(", ")}</p>;
      }
      if (typeof val[0] === "object") {
        return (
          <div className={`${indent} space-y-1`}>
            <p className="font-medium">{label} :</p>
            {val.slice(0, 10).map((item, i) => (
              <div key={i} className="ml-3 pl-2 border-l-2 border-muted/40 space-y-0.5">
                {Object.entries(item).filter(([k]) => !SKIP_KEYS.has(k) && item[k] != null && item[k] !== "").map(([k, v]) => (
                  <div key={k}>{renderValue(k, v, depth + 1)}</div>
                ))}
              </div>
            ))}
          </div>
        );
      }
    }
    if (typeof val === "object") {
      const entries = Object.entries(val).filter(([k]) => !SKIP_KEYS.has(k) && val[k] != null && val[k] !== "");
      if (entries.length === 0) return null;
      return (
        <div className={`${indent} space-y-0.5`}>
          <p className="font-medium">{label} :</p>
          {entries.map(([k, v]) => (
            <div key={k} className="ml-3">{renderValue(k, v, depth + 1)}</div>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderStandaloneEntry = (type, entry) => {
    if (entry.not_shared) {
      return <p className="text-sm text-muted-foreground italic">Non partage par le patient</p>;
    }

    // For exercise_entries with nested data, unwrap it
    let fields = entry;
    if (entry.data && typeof entry.data === "object") {
      fields = { ...entry, ...entry.data };
    }

    const renderedFields = Object.entries(fields)
      .filter(([k, v]) => !SKIP_KEYS.has(k) && k !== "data" && k !== "exercise_type" && v != null && v !== "" && !(Array.isArray(v) && v.length === 0))
      .map(([k, v]) => ({ key: k, node: renderValue(k, v) }))
      .filter(f => f.node !== null);

    if (renderedFields.length === 0) {
      return <p className="text-sm text-muted-foreground italic">Aucune donnee</p>;
    }

    return (
      <div className="text-sm space-y-1.5">
        {renderedFields.map(f => <div key={f.key}>{f.node}</div>)}
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  const patientName = data?.patient?.name || "Mon historique";
  const assignments = data?.assignments || [];
  const standalone = data?.standalone || {};

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="patient-exercises-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {isTherapistViewingPatient && (
              <Link to="/therapist/patients">
                <Button variant="ghost" size="icon" className="flex-shrink-0"><ArrowLeft className="w-5 h-5" /></Button>
              </Link>
            )}
            <div className="min-w-0">
              <h1 className="font-heading text-xl sm:text-2xl font-semibold truncate">
                {isTherapistViewingPatient ? `Exercices — ${patientName}` : t("patientExercises.myExercises")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t("patientExercises.assignedCount", { count: assignments.length })} · {t("patientExercises.standaloneCount", { count: Object.values(standalone).reduce((s, v) => s + v.length, 0) })}
              </p>
            </div>
          </div>
          <Button
            onClick={() => downloadPdf("full")}
            disabled={downloading}
            size="sm"
            className="self-start flex-shrink-0"
            data-testid="download-full-pdf"
          >
            <Download className="w-4 h-4 mr-2" />
            {downloading ? t("patientExercises.generating") : t("patientExercises.downloadPdf")}
          </Button>
        </div>

        {/* Assigned exercises */}
        {assignments.length > 0 && (
          <div>
            <h2 className="font-heading text-lg font-semibold mb-3">{t("patientExercises.assigned")}</h2>
            <div className="space-y-3">
              {assignments.map((a) => {
                const Icon = ICON_MAP[a.exercise_type] || FileText;
                const info = EXERCISE_INFO[a.exercise_type];
                const isExpanded = expandedItems[a.assignment_id];
                const isCompleted = a.status === "completed";

                return (
                  <Card key={a.assignment_id} data-testid={`assignment-${a.assignment_id}`}>
                    <CardContent className="pt-4 pb-4">
                      <div
                        className="flex items-center gap-3 cursor-pointer"
                        onClick={() => toggleExpand(a.assignment_id)}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isCompleted ? "bg-green-100 text-green-600 dark:bg-green-950 dark:text-green-300" : "bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-300"
                        }`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate">{a.title || info?.title || a.exercise_type}</span>
                            {isCompleted ? (
                              <span className="flex items-center gap-1 text-xs text-green-600">
                                <CheckCircle className="w-3 h-3" /> {t("exercise.completed")}
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-xs text-amber-600">
                                <Clock className="w-3 h-3" /> En attente
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">{fmtDate(a.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {isTherapistViewingPatient && (
                            <>
                              <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" onClick={(e) => { e.stopPropagation(); downloadWorksheetPdf(a.exercise_type, { patient_id: patientId }, `fiche-${a.exercise_type}.pdf`).catch(() => toast.error("PDF indisponible")); }} title="Fiche papier dans la langue et pour l'âge du patient" data-testid={`therapist-worksheet-pdf-${a.assignment_id}`}>
                                <Printer className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Fiche papier</span>
                              </Button>
                              <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" onClick={(e) => { e.stopPropagation(); setPersonalize({ type: a.exercise_type, title: a.title || info?.title || a.exercise_type }); }} title="Adapter les exemples grisés à la situation du patient avant impression" data-testid={`therapist-worksheet-custom-${a.assignment_id}`}>
                                <Wand2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Personnaliser</span>
                              </Button>
                            </>
                          )}
                          {user?.role === "patient" && (
                            <Button
                              variant="outline" size="sm" className="gap-1.5 h-8 text-xs"
                              disabled={paperBusy === a.exercise_type}
                              onClick={(e) => { e.stopPropagation(); downloadPaper(a.exercise_type); }}
                              title={t("patientExercises.paperVersionHint")}
                              data-testid={`paper-version-${a.assignment_id}`}
                            >
                              <Printer className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{paperBusy === a.exercise_type ? t("patientExercises.generating") : t("patientExercises.paperVersion")}</span>
                            </Button>
                          )}
                          {isCompleted && (
                            <Button
                              variant="ghost" size="sm"
                              onClick={(e) => { e.stopPropagation(); downloadPdf("single", a.assignment_id); }}
                              data-testid={`download-exercise-${a.assignment_id}`}
                            >
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t space-y-3">
                          {a.instructions && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground uppercase mb-1">Instructions</p>
                              <p className="text-sm">{a.instructions}</p>
                            </div>
                          )}
                          {a.responses?.length > 0 ? (
                            <div>
                              <p className="text-xs font-medium text-green-600 uppercase mb-2">{t("exercise.responses") || "Response(s)"}</p>
                              {a.responses.map((r, i) => (
                                <div key={i} className="bg-green-50 dark:bg-green-950/30 rounded-lg p-3 mb-2">
                                  <p className="text-xs text-muted-foreground mb-2">Soumise le {fmtDate(r.submitted_at)}</p>
                                  {renderResponseData(r.response_data)}
                                </div>
                              ))}
                            </div>
                          ) : isCompleted && standalone[a.exercise_type]?.length > 0 ? (
                            <div>
                              <p className="text-xs font-medium text-green-600 uppercase mb-2">Donnees de l'exercice</p>
                              {standalone[a.exercise_type].slice(0, 5).map((entry, i) => (
                                <div key={i} className="bg-green-50 dark:bg-green-950/30 rounded-lg p-3 mb-2">
                                  <p className="text-xs text-muted-foreground mb-2">{fmtDate(entry.created_at)}</p>
                                  {renderStandaloneEntry(a.exercise_type, entry)}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground italic">{t("exercise.noResponse") || "No response submitted"}</p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Standalone exercises */}
        {Object.keys(standalone).length > 0 && (
          <div>
            <h2 className="font-heading text-lg font-semibold mb-3">{t("patientExercises.standalone")}</h2>
            <div className="space-y-4">
              {Object.entries(standalone).map(([type, entries]) => {
                const Icon = ICON_MAP[type] || FileText;
                const info = EXERCISE_INFO[type];
                const isExpanded = expandedItems[`standalone_${type}`];

                return (
                  <Card key={type} data-testid={`standalone-${type}`}>
                    <CardContent className="pt-4 pb-4">
                      <div
                        className="flex items-center gap-3 cursor-pointer"
                        onClick={() => toggleExpand(`standalone_${type}`)}
                      >
                        <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <span className="font-medium text-sm">{info?.title || type.replace(/_/g, " ")}</span>
                          <p className="text-xs text-muted-foreground">{entries.length} {t("exercise.entries") || "entries"}</p>
                        </div>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                      </div>

                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t space-y-3">
                          {entries.slice(0, 15).map((entry, i) => (
                            <div key={i} className="bg-muted/50 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-xs text-muted-foreground">{fmtDate(entry.created_at)}</p>
                                {isPatientView && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const eid = entry.entry_id || entry.record_id || entry.session_id;
                                      toggleShare(type, eid, !entry.shared_with_therapist);
                                    }}
                                    disabled={togglingShare === (entry.entry_id || entry.record_id || entry.session_id)}
                                    className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full transition-colors ${
                                      entry.shared_with_therapist
                                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 hover:bg-emerald-200"
                                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                                    }`}
                                    data-testid={`share-toggle-${type}-${i}`}
                                  >
                                    {entry.shared_with_therapist ? <Share2 className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                                    {entry.shared_with_therapist ? "Partage" : "Non partage"}
                                  </button>
                                )}
                                {isTherapistViewingPatient && (
                                  <span className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${
                                    entry.shared_with_therapist
                                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                                      : "bg-muted/60 text-muted-foreground"
                                  }`}>
                                    {entry.shared_with_therapist ? <Share2 className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                                    {entry.shared_with_therapist ? "Partage" : "Non partage"}
                                  </span>
                                )}
                              </div>
                              {renderStandaloneEntry(type, entry)}
                            </div>
                          ))}
                          {entries.length > 15 && (
                            <p className="text-xs text-muted-foreground text-center italic">
                              {t("patientExercises.moreEntries", { count: entries.length - 15 })}
                            </p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {assignments.length === 0 && Object.keys(standalone).length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">{t("activation.noActivities") || "No exercises recorded"}</p>
            </CardContent>
          </Card>
        )}
      </div>
      {isTherapistViewingPatient && <WorksheetPersonalizeDialog open={!!personalize} onOpenChange={(o) => { if (!o) setPersonalize(null); }} exerciseType={personalize?.type} exerciseTitle={personalize?.title} patientId={patientId} patientName={(data?.patient?.name || "").split(" ")[0]} />}
    </DashboardLayout>
  );
};

export default PatientExercisesPage;
